import dayjs from 'dayjs'
import { orderBy } from 'lodash'
import {
	API,
	type GraphQLResult,
	graphqlOperation,
} from 'src/utils/amplify/legacy'
import type {
	BankTransactionsByTenantIdQuery,
	ClientsByTenantIdQuery,
	ContactsByTenantIdQuery,
	ExportInfoByTenantIdQuery,
	InvoicesByTenantIdQuery,
	ListProjectsQuery,
	ListUserInfosQuery,
	ModelBankTransactionsFilterInput,
	ModelContactFilterInput,
	ModelExportInfoFilterInput,
	ModelInvoicesFilterInput,
	ModelPreparePurchaseFilterInput,
	ModelProjectsFilterInput,
	ModelProposalsFilterInput,
	ModelPurchaseFilterInput,
	ModelPurchaseOrderFilterInput,
	ModelQuoteFilterInput,
	ModelQuoteInfoFilterInput,
	ModelRecapInfoFilterInput,
	ModelSuppliersFilterInput,
	PreparePurchaseByTenantIdQuery,
	ProjectsByClientIdQuery,
	ProjectsByParentProjectIdQuery,
	ProjectsByTenantIdQuery,
	ProposalsByTenantIdQuery,
	PurchaseOrdersByTenantIdQuery,
	PurchasesByRecapCostlineIdQuery,
	PurchasesByTenantIdQuery,
	QuoteInfoByQuoteIdQuery,
	QuotesByParentProjectIdQuery,
	QuotesByTenantIdQuery,
	RecapInfoInfoByQuoteIdQuery,
	SectionRenameByQuoteIdQuery,
	SuppliersByTenantIdQuery,
} from '@/API'
import type {
	ReduxBankTransactions,
	ReduxClients,
	ReduxContact,
	ReduxExportInfo,
	ReduxInvoices,
	ReduxPreparePurchase,
	ReduxProjects,
	ReduxProposals,
	ReduxPurchase,
	ReduxPurchaseOrder,
	ReduxQuote,
	ReduxQuoteInfo,
	ReduxRecapInfo,
	ReduxSectionRename,
	ReduxSuppliers,
	ReduxUserInfo,
} from '@/types/apiType'
import * as queries from '../graphql/queries'

type Nullable<T> = T | null | undefined

const isNonNullable = <T>(value: Nullable<T>): value is T =>
	value !== null && value !== undefined

type AccessibleItem = {
	tenantId?: string | null
	childFreelancers?: Array<string | null> | null
	parentTeamId?: string | null
	parentSubTeamId?: string | null
}

type TemplateAccessibleItem = {
	tenantId?: string | null
	teamIds?: Array<string | null> | null
	subTeamIds?: Array<string | null> | null
}

type UserInfoLike = {
	groups?: Array<string | null | undefined> | null
	cognitoGroup?: Array<string | null | undefined> | null
	currentCompanyTenantId?: string | null | undefined
	tenantId?: string | null | undefined
	teamIds?: Array<string | null | undefined> | null
	subTeamIds?: Array<string | null | undefined> | null
}

const getUserGroups = (userInfo: UserInfoLike | null | undefined): string[] => {
	if (!userInfo) {
		return []
	}
	if (Array.isArray(userInfo.groups)) {
		return userInfo.groups.filter(isNonNullable)
	}
	if (Array.isArray(userInfo.cognitoGroup)) {
		return userInfo.cognitoGroup.filter(isNonNullable)
	}
	return []
}

export const quotesByTenantId = /* GraphQL */ `
  query QuotesByTenantId(
    $tenantId: ID!
    $sortDirection: ModelSortDirection
    $filter: ModelQuoteFilterInput
    $limit: Int
    $nextToken: String
  ) {
    quotesByTenantId(
      tenantId: $tenantId
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        name
        tenantId
        quoteType
        childFreelancers
        offlineIds
        activeFlag
        type
        parentProjectId
        billLocation
        marginPercentage
        overheadPercentage
        exchangeRate
        allowInternational
        freelancers
        parentCompanyId
        maximizeMinimize
        clientName
        projectName
        agency
        agencyRepresentative
        director
        executiveProducer
        lineProducer
        numberOfShootingDays
        notes
        introduction
        shooting
        prep
        shootingCountry
        prelight
        casting
        crew
        buyouts
        locations
        studio
        artDepartment
        wardrobe
        equipment
        travel
        accommodation
        editing
        imagePostproduction
        soundPostproduction
        deliverables
        insurance
        quoteGuidelines
        excluded
        paymentTerms
        miscellaneous
        installment1Percentage
        installment1Date
        installment2Percentage
        installment2Date
        balancePercentage
        balanceDate
        isStarred
        isChecked
        recapInfoComments
        contact
        discount {
          totalDiscount
          totalAfterDiscount
          totalDiscountOnMargin
          totalDiscountOnMarginPercentage
          totalDiscountPercentage
        }
        advertiserJobID
        brand
        jobName
        agencyJobID
        product
        advertiser
        productionJobID
        advertiserAddress
        productionCompany
        productionContact
        vfxCo
        vfxProducer
        editorialCo
        editorialProducer
        musicCo
        musicProducer
        agencyProducer
        agencyCreativeTeam
        prepDays
        buildAndStrikeDays
        preLight
        stageDays
        locationDays
        createdOn
        updatedOn
        _version
        _deleted
        _lastChangedAt
      }
      nextToken
    }
  }
`

export const quotesByTenantIdNew = /* GraphQL */ `
  query QuotesByTenantId(
    $tenantId: ID!
    $sortDirection: ModelSortDirection
    $filter: ModelQuoteFilterInput
    $limit: Int
    $nextToken: String
  ) {
    quotesByTenantId(
      tenantId: $tenantId
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        name
        tenantId
        quoteType
        quoteVersion
        blankQuoteType
        parentTeamId
        parentSubTeamId
        visibility
        quoteLanguage
        childFreelancers
        offlineIds
        activeFlag
        type
        mergedQuotes
        parentProjectId
        billLocation
        marginLabel
        overheadLabel
        marginAndOverheadLabel
        marginPercentage
        overheadPercentage
        exchangeRate
        localExchangeRate
        localOtNumberOfHours
        exeOtNumberOfHours
        marginVAT_rate
        marginAcc_section
        allowInternational
        freelancers
        parentCompanyId
        companyLogo
        maximizeMinimize
        provisionalColumn
        selectContactById
        clientId
        contactId
        clientName
        projectName
        agency
        agencyRepresentative
        director
        executiveProducer
        lineProducer
        numberOfShootingDays
        notes
        introduction
        shooting
        prep
        shootingCountry
        prelight
        casting
        crew
        buyouts
        locations
        studio
        artDepartment
        wardrobe
        equipment
        travel
        accommodation
        editing
        imagePostproduction
        soundPostproduction
        deliverables
        insurance
        quoteGuidelines
        excluded
        paymentTerms
        miscellaneous
        conclusion
        installment1Percentage
        installment1Date
        installment2Percentage
        installment2Date
        balancePercentage
        balanceDate
        isStarred
        isChecked
        isFinalized
        recapInfoComments
        contact
        quoteTotal {
          budgetSubTotal
          budgetGrandTotal
          budgetMargin
          budgetMarginPercentage
          budgetSeperateMargin
          budgetSeperateMarginPercentage
          budgetSeperateOverhead
          budgetSeperateOverheadPercentage
          provisionalSubTotal
          provisionalGrandTotal
          provisionalMargin
          provisionalMarginPercentage
          actualSubTotal
          actualGrandTotal
          actualMargin
          actualMarginPercentage
          purchaseSubTotal
          purchaseGrandTotal
          purchaseMargin
          purchaseMarginPercentage
          grandBudgetWithMarginTotal
          noMarginTotalPercentage
          subTotalBudgetWithMargin
          subTotalBudgetWithoutMargin
          subTotalBudgetWithMarginPercentage
        }
        quoteTotalDiscount {
          totalAfterDiscount
          totalDiscount
          budgetTotalDiscountOnMargin
          budgetTotalDiscountOnMarginPercentage
          budgetTotalDiscountPercentage
          provisionalTotalDiscountOnMargin
          provisionalTotalDiscountOnMarginPercentage
          provisionalTotalDiscountPercentage
          actualTotalDiscountOnMargin
          actualTotalDiscountOnMarginPercentage
          actualTotalDiscountPercentage
          purchaseTotalDiscountOnMargin
          purchaseTotalDiscountOnMarginPercentage
          purchaseTotalDiscountPercentage
        }
        discount {
          totalDiscount
          totalAfterDiscount
          totalDiscountOnMargin
          totalDiscountOnMarginPercentage
          totalDiscountPercentage
        }
        quoteNumber
        quoteAccountingCategory
        deliveryDate
        quoteTertiaryCategory
        exeLocation
        advertiserJobID
        brand
        jobName
        agencyJobID
        product
        advertiser
        productionJobID
        advertiserAddress
        productionCompany
        productionContact
        vfxCo
        vfxProducer
        editorialCo
        editorialProducer
        musicCo
        musicProducer
        agencyProducer
        agencyCreativeTeam
        prepDays
        buildAndStrikeDays
        preLight
        stageDays
        locationDays
        description
        service
        scope
        vhr
        billingAddress
        dateOfShoot
        photographer
        jobNumber
        photoLocation
        quoteProdValue1
        quoteProdValue2
        quoteProdValue3
        quoteProdValue4
        quoteProdValue5
        quoteProdValue6
        quoteProdValue7
        quoteProdValue8
        quoteProdValue9
        quoteProdValue10
        quoteProdValue11
        quoteProdValue12
        quoteProdValue13
        quoteProdValue14
        quoteProdValue15
        quoteProdValue16
        quoteProdValue17
        quoteProdValue18
        quoteProdValue19
        quoteProdValue20
        quoteProdValue21
        quoteProdValue22
        quoteProdValue23
        quoteProdValue24
        quoteProdValue25
        termsType
        termsSelection
        quoteCurrency
        marginCustomization
        pdfColumnCustomization
        summaryTableCustomization
        quoteHeaderLabel1
        quoteHeaderValue1
        quoteHeaderLabel2
        quoteHeaderValue2
        quoteHeaderLabel3
        quoteHeaderValue3
        quoteHeaderLabel4
        quoteHeaderValue4
        quoteHeaderLabel5
        quoteHeaderValue5
        quoteHeaderLabel6
        quoteHeaderValue6
        quoteHeaderLabel7
        quoteHeaderValue7
        quoteHeaderLabel8
        quoteHeaderValue8
        quoteHeaderLabel9
        quoteHeaderValue9
        quoteHeaderLabel10
        quoteHeaderValue10
        quoteHeaderLabel11
        quoteHeaderValue11
        budgetSections
        assignedTenantId
        assignedTenantName
        isExpired
        proposals {
          items {
            id
            tenantId
            activeFlag
            quoteId
            quoteType
            clientId
            contactId
            status
            noofInvoices
            quoteName
            quoteNumber
            projectName
            projectNumber
            clientTaxType
            clientName
            contactName
            clientEmail
            clientAddress
            clientCountry
            clientIdNumber
            clientVatNumber
            clientCurrency
            clientTaxRate
            paymentTerms
            total
            clientNote
            isDetailed
            isClientTaxRate
            bankProfileNumber
            proposalsBankProfileName
            proposalsBank
            proposalsIban
            proposalsBic
            proposalsAccountName
            createdOn
            updatedOn
            poDate
            poName
            poAmount
            poCurrency
            poNotes
            assignedTenantId
            assignedTenantName
            invoices {
              items {
                id
                tenantId
                proposalId
                quoteType
                activeFlag
                originalInvoiceId
                creditInvoiceGenerated
                archivedDate
                paidDate
                paymentStatus
                quoteName
                quoteNumber
                projectName
                projectNumber
                clientId
                contactId
                clientName
                contactName
                clientTaxType
                clientEmail
                clientAddress
                clientCountry
                clientIdNumber
                clientVatNumber
                clientCurrency
                clientTaxRate
                title
                invoiceNumber
                poNumber
                invoiceAmountPercentage
                amount
                amountAfterTax
                taxAmount
                balancePercentage
                balanceAmount
                clientNote
                isDetailed
                isClientTaxRate
                bankProfileNumber
                invoicesBankProfileName
                invoicesBank
                invoicesIban
                invoicesBic
                invoicesAccountName
                invoiceDate
                invoiceDueDate
                description
                creditComments
                invoiceType
                salesAccNumberType
                createdOn
                updatedOn
                uniqueTransactionId
                assignedTenantId
                assignedTenantName
                showBidLetter
                _version
                _deleted
                _lastChangedAt
              }
              nextToken
            }
            drafts {
              generated
              poNumber
              proposalAmountPercentage
              amount
              amountAfterTax
              taxAmount
              balancePercentage
              balanceAmount
              invoiceId
              invoiceDate
              invoiceDueDate
              description
              readyStatus
              taxDetails {
                taxRate
                taxPercentage
                taxAmount
                taxableAmount
              }
              salesAccNumberType
              sectionDetails {
                type
                amount
              }
              sectionDetailsByQuote {
                type
                amount
              }
              sectionDetailsByAccSection {
                type
                amount
              }
            }

            _version
            _deleted
            _lastChangedAt
          }
          nextToken
        }
        createdOn
        updatedOn
        _version
        _deleted
        _lastChangedAt
      }
      nextToken
    }
  }
`

export const getProjects = /* GraphQL */ `
  query GetProjects($id: ID!) {
    getProjects(id: $id) {
      id
      name
      tenantId
      freelancers
      childFreelancers
      offlineIds
      parentProjectId
      parentClientId
      activeFlag
      type
      parentTeamId
      parentSubTeamId
      isSubTeam
      projectType
      projectGroup
      parentCompanyId
      projectNumber
      subTeamCount
      childProjects {
        items {
          id
          name
          tenantId
          freelancers
          childFreelancers
          offlineIds
          parentProjectId
          parentClientId
          activeFlag
          type
          parentTeamId
          parentSubTeamId
          isSubTeam
          projectType
          projectGroup
          parentCompanyId
          projectNumber
          subTeamCount
          childProjects {
            nextToken
          }
          quotes {
            nextToken
          }
          createdOn
          updatedOn
          _version
          _deleted
          _lastChangedAt
        }
        nextToken
      }
        nextToken
      }
      createdOn
      updatedOn
      _version
      _deleted
      _lastChangedAt
    }
  }
`

async function fetchClientsUtil(
	tenantId?: string | null
): Promise<ReduxClients[]> {
	if (!tenantId) {
		return []
	}
	const clientsArray: ReduxClients[] = []
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchClients = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const data = (await API.graphql(
			graphqlOperation(queries.clientsByTenantId, {
				nextToken,
				tenantId,
				filter: { activeFlag: { eq: true } },
				limit: 1000,
			})
		)) as GraphQLResult<ClientsByTenantIdQuery>
		const clients = data?.data?.clientsByTenantId?.items
		nextToken = data?.data?.clientsByTenantId?.nextToken ?? null
		counter++
		if (clients) {
			const sanitizedClients = clients.filter(isNonNullable)
			clientsArray.push(
				...(sanitizedClients as unknown as ReduxClients[])
			)
		}
		return paginateFetchClients(nextToken)
	}
	await paginateFetchClients()
	return clientsArray
}

async function fetchClientsUtilForTeamAdmin({
	tenantId,
	teamIds,
}: {
	tenantId?: string | null
	teamIds?: string[] | null
}): Promise<ReduxClients[]> {
	if (!tenantId || !teamIds) {
		return []
	}
	const clientsArray: ReduxClients[] = []
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchClients = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const data = (await API.graphql(
			graphqlOperation(queries.clientsByTenantId, {
				nextToken,
				tenantId,
				filter: { activeFlag: { eq: true } },
				limit: 1000,
			})
		)) as GraphQLResult<ClientsByTenantIdQuery>
		const clients = data?.data?.clientsByTenantId?.items
		const nextTokenFromApi =
			data?.data?.clientsByTenantId?.nextToken ?? null
		counter++
		if (clients) {
			const sanitizedClients = clients.filter(isNonNullable)
			const normalizedTeamIds = (teamIds ?? []).filter(
				(teamId): teamId is string =>
					typeof teamId === 'string' && teamId.length > 0
			)
			const filteredClients = sanitizedClients.filter((client: any) =>
				normalizedTeamIds.includes(client.parentTeamId ?? '')
			)
			clientsArray.push(...(filteredClients as unknown as ReduxClients[]))
		}
		return paginateFetchClients(nextTokenFromApi)
	}
	await paginateFetchClients()
	return clientsArray
}

async function fetchProjectsByTenantIdUtil(
	tenantId?: string | null
): Promise<ReduxProjects[]> {
	if (!tenantId) {
		return []
	}
	const projectsArray: ReduxProjects[] = []
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchProjects = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const data = (await API.graphql(
			graphqlOperation(queries.projectsByTenantId, {
				nextToken,
				tenantId,
				filter: { activeFlag: { eq: true } },
				limit: 1000,
			})
		)) as GraphQLResult<ProjectsByTenantIdQuery>
		const projects = data?.data?.projectsByTenantId?.items
		nextToken = data?.data?.projectsByTenantId?.nextToken ?? null
		counter++
		if (projects) {
			const sanitizedProjects = projects.filter(isNonNullable)
			projectsArray.push(
				...(sanitizedProjects as unknown as ReduxProjects[])
			)
		}
		return paginateFetchProjects(nextToken)
	}
	await paginateFetchProjects()
	return projectsArray
}

async function fetchProjectsByTenantIdUtilForTeamAdmin({
	tenantId,
	teamIds,
}: {
	tenantId?: string | null
	teamIds?: string[] | null
}): Promise<ReduxProjects[]> {
	if (!tenantId || !teamIds) {
		return []
	}
	const projectsArray: ReduxProjects[] = []
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchProjects = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const data = (await API.graphql(
			graphqlOperation(queries.projectsByTenantId, {
				nextToken,
				tenantId,
				filter: { activeFlag: { eq: true } },
				limit: 1000,
			})
		)) as GraphQLResult<ProjectsByTenantIdQuery>
		const projects = data?.data?.projectsByTenantId?.items
		nextToken = data?.data?.projectsByTenantId?.nextToken ?? null
		counter++
		if (projects) {
			const sanitizedProjects = projects.filter(isNonNullable)
			const normalizedTeamIds = (teamIds ?? []).filter(
				(teamId): teamId is string =>
					typeof teamId === 'string' && teamId.length > 0
			)
			const filteredProjects = sanitizedProjects.filter((project: any) =>
				normalizedTeamIds.includes(project.parentTeamId ?? '')
			)
			projectsArray.push(
				...(filteredProjects as unknown as ReduxProjects[])
			)
		}
		return paginateFetchProjects(nextToken)
	}
	await paginateFetchProjects()
	return projectsArray
}

async function fetchProjectsByTenantIdUtilForSubTeamAdmin({
	tenantId,
	subTeamIds,
}: {
	tenantId?: string | null
	subTeamIds?: string[] | null
}): Promise<ReduxProjects[]> {
	if (!tenantId || !subTeamIds) {
		return []
	}
	const projectsArray: ReduxProjects[] = []
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchProjects = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const data = (await API.graphql(
			graphqlOperation(queries.projectsByTenantId, {
				nextToken,
				tenantId,
				filter: { activeFlag: { eq: true } },
				limit: 1000,
			})
		)) as GraphQLResult<ProjectsByTenantIdQuery>
		const projects = data?.data?.projectsByTenantId?.items
		nextToken = data?.data?.projectsByTenantId?.nextToken ?? null
		counter++
		if (projects) {
			const sanitizedProjects = projects.filter(isNonNullable)
			const normalizedSubTeamIds = (subTeamIds ?? []).filter(
				(subTeamId): subTeamId is string =>
					typeof subTeamId === 'string' && subTeamId.length > 0
			)
			const filteredProjects = sanitizedProjects.filter((project: any) =>
				normalizedSubTeamIds.includes(project.parentSubTeamId ?? '')
			)
			projectsArray.push(
				...(filteredProjects as unknown as ReduxProjects[])
			)
		}
		return paginateFetchProjects(nextToken)
	}
	await paginateFetchProjects()
	return projectsArray
}

async function fetchAllProjectsByTenantIdUtil(
	tenantId?: string | null
): Promise<ReduxProjects[]> {
	if (!tenantId) {
		return []
	}
	const projectsArray: ReduxProjects[] = []
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchProjects = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const data = (await API.graphql(
			graphqlOperation(queries.projectsByTenantId, {
				nextToken,
				tenantId,
				limit: 1000,
			})
		)) as GraphQLResult<ProjectsByTenantIdQuery>
		const projects = data?.data?.projectsByTenantId?.items
		nextToken = data?.data?.projectsByTenantId?.nextToken ?? null
		counter++
		if (projects) {
			const sanitizedProjects = projects.filter(isNonNullable)
			projectsArray.push(
				...(sanitizedProjects as unknown as ReduxProjects[])
			)
		}
		return paginateFetchProjects(nextToken)
	}
	await paginateFetchProjects()
	return projectsArray
}

async function fetchArchiveProjectsByTenantIdUtil(
	tenantId?: string | null
): Promise<ReduxProjects[]> {
	if (!tenantId) {
		return []
	}
	const projectsArray: ReduxProjects[] = []
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchProjects = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const data = (await API.graphql(
			graphqlOperation(queries.projectsByTenantId, {
				nextToken,
				tenantId,
				filter: {
					and: [
						{ type: { eq: 'archive' } },
						{ activeFlag: { eq: true } },
						{
							_deleted: {
								attributeExists: false,
							},
						},
					],
				},
				limit: 1000,
			})
		)) as GraphQLResult<ProjectsByTenantIdQuery>
		const projects = data?.data?.projectsByTenantId?.items
		nextToken = data?.data?.projectsByTenantId?.nextToken ?? null
		counter++
		if (projects) {
			const sanitizedProjects = projects.filter(isNonNullable)
			projectsArray.push(
				...(sanitizedProjects as unknown as ReduxProjects[])
			)
		}
		return paginateFetchProjects(nextToken)
	}
	await paginateFetchProjects()
	return projectsArray
}

async function fetchProjectsByClientIdUtil(
	parentClientId: string | null | undefined,
	filter?: ModelProjectsFilterInput | null
): Promise<ReduxProjects[]> {
	if (!parentClientId) {
		return []
	}
	const projectsArray: ReduxProjects[] = []
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchProjects = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const data = (await API.graphql(
			graphqlOperation(queries.projectsByClientId, {
				nextToken,
				parentClientId,
				filter,
				limit: 1000,
			})
		)) as GraphQLResult<ProjectsByClientIdQuery>
		const projects = data?.data?.projectsByClientId?.items
		nextToken = data?.data?.projectsByClientId?.nextToken ?? null
		counter++
		if (projects) {
			const sanitizedProjects = projects.filter(isNonNullable)
			projectsArray.push(
				...(sanitizedProjects as unknown as ReduxProjects[])
			)
		}
		return paginateFetchProjects(nextToken)
	}
	await paginateFetchProjects()
	return projectsArray
}

async function fetchProjectsByParentProjectIdUtil(
	parentProjectId: string | null | undefined,
	filter?: ModelProjectsFilterInput | null
): Promise<ReduxProjects[]> {
	if (!parentProjectId) {
		return []
	}
	const projectsArray: ReduxProjects[] = []
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchProjects = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const data = (await API.graphql(
			graphqlOperation(queries.projectsByParentProjectId, {
				nextToken,
				parentProjectId,
				filter,
				limit: 1000,
			})
		)) as GraphQLResult<ProjectsByParentProjectIdQuery>
		const projects = data?.data?.projectsByParentProjectId?.items
		nextToken = data?.data?.projectsByParentProjectId?.nextToken ?? null
		counter++
		if (projects) {
			const sanitizedProjects = projects.filter(isNonNullable)
			projectsArray.push(
				...(sanitizedProjects as unknown as ReduxProjects[])
			)
		}
		return paginateFetchProjects(nextToken)
	}
	await paginateFetchProjects()
	return projectsArray
}

async function fetchQuotesByTenantIdUtil(
	tenantId: string | null | undefined
): Promise<ReduxQuote[]> {
	if (!tenantId) {
		return []
	}
	const quotesArray: ReduxQuote[] = []
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchQuotes = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}

		const data = (await API.graphql(
			graphqlOperation(quotesByTenantId, {
				nextToken,
				tenantId,
				filter: { activeFlag: { eq: true } },
				limit: 1000,
			})
		)) as GraphQLResult<QuotesByTenantIdQuery>

		const projects =
			data?.data?.quotesByTenantId?.items?.filter(isNonNullable) ?? []
		nextToken = data?.data?.quotesByTenantId?.nextToken ?? null
		counter++
		quotesArray.push(...(projects as unknown as ReduxQuote[]))
		return paginateFetchQuotes(nextToken)
	}
	await paginateFetchQuotes()
	return quotesArray
}

async function fetchAllQuotesByTenantIdUtil(
	tenantId: string | null | undefined
): Promise<ReduxQuote[]> {
	if (!tenantId) {
		return []
	}
	const quotesArray: ReduxQuote[] = []
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchQuotes = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}

		const data = (await API.graphql(
			graphqlOperation(quotesByTenantId, {
				nextToken,
				tenantId,
				limit: 1000,
			})
		)) as GraphQLResult<QuotesByTenantIdQuery>

		const projects =
			data?.data?.quotesByTenantId?.items?.filter(isNonNullable) ?? []
		nextToken = data?.data?.quotesByTenantId?.nextToken ?? null
		counter++
		quotesArray.push(...(projects as unknown as ReduxQuote[]))
		return paginateFetchQuotes(nextToken)
	}
	await paginateFetchQuotes()
	return quotesArray
}
async function fetchAllQuotesByTenantIdUtilNew(
	tenantId: string | null | undefined,
	startDate: string | null | undefined,
	endDate: string | null | undefined
): Promise<ReduxQuote[]> {
	if (!tenantId) {
		return []
	}
	const quotesArray: ReduxQuote[] = []
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchQuotes = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}

		let currFilter: ModelQuoteFilterInput = {
			activeFlag: { eq: true },
			and: [
				{
					createdOn: {
						gt: startDate ?? dayjs().toISOString(),
					},
				},
				{ createdOn: { lt: endDate ?? dayjs().toISOString() } },
				{
					_deleted: {
						attributeExists: false,
					},
				},
			],
		}
		if (!startDate || !endDate) {
			currFilter = { activeFlag: { eq: true } }
		}

		const data = (await API.graphql(
			graphqlOperation(quotesByTenantIdNew, {
				nextToken,
				tenantId,
				filter: currFilter,
				limit: 1000,
			})
		)) as GraphQLResult<QuotesByTenantIdQuery>

		const projects =
			data?.data?.quotesByTenantId?.items?.filter(isNonNullable) ?? []
		nextToken = data?.data?.quotesByTenantId?.nextToken ?? null
		counter++
		quotesArray.push(...(projects as unknown as ReduxQuote[]))
		return paginateFetchQuotes(nextToken)
	}
	await paginateFetchQuotes()
	return quotesArray
}

async function fetchAllQuotesByTenantIdUtilForTeamAdmin({
	tenantId,
	teamIds,
	startDate,
	endDate,
}: {
	tenantId?: string | null
	teamIds?: string[] | null
	startDate?: string | null
	endDate?: string | null
}): Promise<ReduxQuote[]> {
	if (!tenantId || !teamIds) {
		return []
	}
	const quotesArray: ReduxQuote[] = []
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchQuotes = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		let currFilter: ModelQuoteFilterInput = {
			activeFlag: { eq: true },
			and: [
				{
					createdOn: {
						gt: startDate ?? dayjs().toISOString(),
					},
				},
				{ createdOn: { lt: endDate ?? dayjs().toISOString() } },
				{
					_deleted: {
						attributeExists: false,
					},
				},
			],
		}
		if (!startDate || !endDate) {
			currFilter = { activeFlag: { eq: true } }
		}

		const data = (await API.graphql(
			graphqlOperation(quotesByTenantIdNew, {
				nextToken,
				tenantId,
				filter: currFilter,
				limit: 1000,
			})
		)) as GraphQLResult<QuotesByTenantIdQuery>

		const quotes =
			data?.data?.quotesByTenantId?.items?.filter(isNonNullable) ?? []
		const nextTokenApi = data?.data?.quotesByTenantId?.nextToken ?? null
		counter++
		const normalizedTeamIds = (teamIds ?? []).filter(
			(teamId): teamId is string =>
				typeof teamId === 'string' && teamId.length > 0
		)
		const filteredQuotes = quotes.filter((quote: any) =>
			normalizedTeamIds.includes(quote.parentTeamId ?? '')
		)
		quotesArray.push(...(filteredQuotes as unknown as ReduxQuote[]))
		return paginateFetchQuotes(nextTokenApi)
	}
	await paginateFetchQuotes()
	return quotesArray
}

async function fetchAllQuotesByTenantIdUtilForSubTeamAdmin({
	tenantId,
	subTeamIds,
	startDate,
	endDate,
}: {
	tenantId?: string | null
	subTeamIds?: string[] | null
	startDate?: string | null
	endDate?: string | null
}): Promise<ReduxQuote[]> {
	if (!tenantId || !subTeamIds) {
		return []
	}
	const quotesArray: ReduxQuote[] = []
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchQuotes = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		let currFilter: ModelQuoteFilterInput = {
			activeFlag: { eq: true },
			and: [
				{
					createdOn: {
						gt: startDate ?? dayjs().toISOString(),
					},
				},
				{ createdOn: { lt: endDate ?? dayjs().toISOString() } },
				{
					_deleted: {
						attributeExists: false,
					},
				},
			],
		}
		if (!startDate || !endDate) {
			currFilter = { activeFlag: { eq: true } }
		}

		const data = (await API.graphql(
			graphqlOperation(quotesByTenantIdNew, {
				nextToken,
				tenantId,
				filter: currFilter,
				limit: 1000,
			})
		)) as GraphQLResult<QuotesByTenantIdQuery>

		const quotes =
			data?.data?.quotesByTenantId?.items?.filter(isNonNullable) ?? []
		const nextTokenApi = data?.data?.quotesByTenantId?.nextToken ?? null
		counter++

		const normalizedSubTeamIds = (subTeamIds ?? []).filter(
			(subTeamId): subTeamId is string =>
				typeof subTeamId === 'string' && subTeamId.length > 0
		)
		const filteredQuotes = quotes.filter((quote: any) =>
			normalizedSubTeamIds.includes(quote.parentSubTeamId ?? '')
		)
		quotesArray.push(...(filteredQuotes as unknown as ReduxQuote[]))
		return paginateFetchQuotes(nextTokenApi)
	}
	await paginateFetchQuotes()
	return quotesArray
}

async function fetchQuotesByParentProjectIdUtil(
	parentProjectId: string | null | undefined,
	filter?: ModelQuoteFilterInput | null
): Promise<ReduxQuote[]> {
	if (!parentProjectId) {
		return []
	}
	const quotesArray: ReduxQuote[] = []
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchQuotes = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const data = (await API.graphql(
			graphqlOperation(queries.quotesByParentProjectId, {
				nextToken,
				parentProjectId,
				filter,
				limit: 1000,
			})
		)) as GraphQLResult<QuotesByParentProjectIdQuery>
		const quotes = data?.data?.quotesByParentProjectId?.items
		nextToken = data?.data?.quotesByParentProjectId?.nextToken ?? null
		counter++
		if (quotes) {
			const sanitizedQuotes = quotes.filter(isNonNullable)
			quotesArray.push(...(sanitizedQuotes as unknown as ReduxQuote[]))
		}
		return paginateFetchQuotes(nextToken)
	}
	await paginateFetchQuotes()
	return quotesArray
}

async function fetchQuoteInfoUtil(
	quoteId: string | null | undefined,
	filter?: ModelQuoteInfoFilterInput | null
): Promise<ReduxQuoteInfo[]> {
	if (!quoteId) {
		return []
	}
	const quoteInfoArray: ReduxQuoteInfo[] = []
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchQuoteInfo = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const data = (await API.graphql(
			graphqlOperation(queries.quoteInfoByQuoteId, {
				nextToken,
				quoteId,
				filter,
				limit: 1000,
			})
		)) as GraphQLResult<QuoteInfoByQuoteIdQuery>
		const quote = data?.data?.quoteInfoByQuoteId?.items
		nextToken = data?.data?.quoteInfoByQuoteId?.nextToken ?? null
		counter++
		if (quote) {
			const sanitizedQuoteInfo = quote.filter(isNonNullable)
			quoteInfoArray.push(
				...(sanitizedQuoteInfo as unknown as ReduxQuoteInfo[])
			)
		}
		return paginateFetchQuoteInfo(nextToken)
	}
	await paginateFetchQuoteInfo()
	return orderBy<ReduxQuoteInfo>(
		quoteInfoArray,
		['fractionalSortKey'],
		['asc']
	)
}

async function fetchRecapInfoUtil(
	quoteId: string | null | undefined,
	filter?: ModelRecapInfoFilterInput | null
): Promise<ReduxRecapInfo[]> {
	if (!quoteId) {
		return []
	}
	const recapInfoArray: ReduxRecapInfo[] = []
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchRecapInfo = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const data = (await API.graphql(
			graphqlOperation(queries.recapInfoInfoByQuoteId, {
				quoteId,
				filter,
				limit: 3000,
				nextToken,
			})
		)) as GraphQLResult<RecapInfoInfoByQuoteIdQuery>
		const recapQuote =
			data?.data?.recapInfoInfoByQuoteId?.items?.filter(isNonNullable) ??
			[]
		const nextTokenFromApi =
			data?.data?.recapInfoInfoByQuoteId?.nextToken ?? null
		counter++
		recapInfoArray.push(...(recapQuote as unknown as ReduxRecapInfo[]))
		return paginateFetchRecapInfo(nextTokenFromApi)
	}

	await paginateFetchRecapInfo()
	return orderBy<ReduxRecapInfo>(
		recapInfoArray,
		['fractionalSortKey'],
		['asc']
	)
}

async function fetchSectionRenameUtil(
	quoteId: string | null | undefined
): Promise<ReduxSectionRename[]> {
	if (!quoteId) {
		return []
	}
	const sectionRenameArray: ReduxSectionRename[] = []
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchSectionRename = async (
		nextToken: string | null = null
	) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const data = (await API.graphql(
			graphqlOperation(queries.sectionRenameByQuoteId, {
				nextToken,
				quoteId,
				limit: 1000,
			})
		)) as GraphQLResult<SectionRenameByQuoteIdQuery>
		const sectionRename =
			data?.data?.sectionRenameByQuoteId?.items?.filter(isNonNullable) ??
			[]
		const nextTokenFromApi =
			data?.data?.sectionRenameByQuoteId?.nextToken ?? null
		counter++
		sectionRenameArray.push(
			...(sectionRename as unknown as ReduxSectionRename[])
		)
		return paginateFetchSectionRename(nextTokenFromApi)
	}
	await paginateFetchSectionRename()

	return sectionRenameArray
}

async function fetchRecapInfoWithActiveFlagFalse(
	quoteId: string | null | undefined
): Promise<ReduxRecapInfo[]> {
	if (!quoteId) {
		return []
	}
	const recapInfoArray: ReduxRecapInfo[] = []
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchRecapInfo = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const data = (await API.graphql(
			graphqlOperation(queries.recapInfoInfoByQuoteId, {
				quoteId,
				limit: 3000,
				nextToken,
			})
		)) as GraphQLResult<RecapInfoInfoByQuoteIdQuery>
		const recapQuote =
			data?.data?.recapInfoInfoByQuoteId?.items?.filter(isNonNullable) ??
			[]
		const nextTokenFromApi =
			data?.data?.recapInfoInfoByQuoteId?.nextToken ?? null
		counter++
		recapInfoArray.push(...(recapQuote as unknown as ReduxRecapInfo[]))
		return paginateFetchRecapInfo(nextTokenFromApi)
	}

	await paginateFetchRecapInfo()
	return recapInfoArray
}

async function fetchFreelancerProjectUtil({
	tenantId,
	companyTenantId,
}: {
	tenantId?: string | null
	companyTenantId?: string | null
}): Promise<ReduxProjects[]> {
	if (!tenantId) {
		return []
	}
	const freelancerProjectArray: ReduxProjects[] = []
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchFreelancersProjects = async (
		nextToken: string | null = null
	) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		let filter: ModelProjectsFilterInput = {
			activeFlag: { eq: true },
			freelancers: { contains: tenantId },
		}
		if (companyTenantId) {
			filter = {
				activeFlag: { eq: true },
				freelancers: { contains: tenantId },
				or: [
					{ tenantId: { eq: companyTenantId } },
					{ type: { eq: 'archive' } },
				],
			}
		}

		const response = (await API.graphql(
			graphqlOperation(queries.listProjects, {
				nextToken,
				filter,
				limit: 1000,
			})
		)) as GraphQLResult<ListProjectsQuery>

		const freelancerProjects =
			response.data?.listProjects?.items?.filter(isNonNullable) ?? []
		const nextTokenFromApi = response.data?.listProjects?.nextToken ?? null
		counter++
		freelancerProjectArray.push(
			...(freelancerProjects as unknown as ReduxProjects[])
		)
		return paginateFetchFreelancersProjects(nextTokenFromApi)
	}
	await paginateFetchFreelancersProjects()

	return freelancerProjectArray
}

async function fetchChildFreelancerProjectUtil({
	freelancerTenantId,
	companyTenantId,
}: {
	freelancerTenantId?: string | null
	companyTenantId?: string | null
}): Promise<ReduxProjects[]> {
	if (!freelancerTenantId) {
		return []
	}
	const childFreelancerProjectArray: ReduxProjects[] = []
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchChildFreelancersProjects = async (
		nextToken: string | null = null
	) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		let filter: ModelProjectsFilterInput
		if (!companyTenantId || companyTenantId === freelancerTenantId) {
			filter = {
				activeFlag: { eq: true },
				or: [
					{ childFreelancers: { contains: freelancerTenantId } },
					{ tenantId: { eq: freelancerTenantId } },
				],
			}
		} else {
			filter = {
				activeFlag: { eq: true },
				or: [
					{
						and: [
							{ tenantId: { eq: companyTenantId } },
							{
								childFreelancers: {
									contains: freelancerTenantId,
								},
							},
							{
								_deleted: {
									attributeExists: false,
								},
							},
						],
					},
					{ tenantId: { eq: freelancerTenantId } },
				],
			}
		}
		const response = (await API.graphql(
			graphqlOperation(queries.listProjects, {
				nextToken,
				filter,
				limit: 1000,
			})
		)) as GraphQLResult<ListProjectsQuery>

		const childFreelancerProjects =
			response.data?.listProjects?.items?.filter(isNonNullable) ?? []
		const nextTokenFromApi = response.data?.listProjects?.nextToken ?? null
		counter++
		childFreelancerProjectArray.push(
			...(childFreelancerProjects as unknown as ReduxProjects[])
		)
		return paginateFetchChildFreelancersProjects(nextTokenFromApi)
	}
	await paginateFetchChildFreelancersProjects()

	return childFreelancerProjectArray
}

async function fetchSuppliersByTenantIdUtil(
	tenantId: string | null | undefined
): Promise<ReduxSuppliers[]> {
	if (!tenantId) {
		return []
	}
	const suppliersArray: ReduxSuppliers[] = []
	let counter = 0
	const supplierFilter: ModelSuppliersFilterInput = {
		activeFlag: { eq: true },
	}
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchSuppliers = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const response = (await API.graphql(
			graphqlOperation(queries.suppliersByTenantId, {
				nextToken,
				tenantId,
				filter: supplierFilter,
				limit: 1000,
			})
		)) as GraphQLResult<SuppliersByTenantIdQuery>
		const suppliers =
			response.data?.suppliersByTenantId?.items?.filter(isNonNullable) ??
			[]
		const nextTokenFromApi =
			response.data?.suppliersByTenantId?.nextToken ?? null
		counter++
		suppliersArray.push(...(suppliers as unknown as ReduxSuppliers[]))
		return paginateFetchSuppliers(nextTokenFromApi)
	}
	await paginateFetchSuppliers()
	return suppliersArray
}

async function fetchPurchasesByTenantIdUtil({
	tenantId,
	startDate = null,
	endDate = null,
}: {
	tenantId?: string | null
	startDate?: string | null
	endDate?: string | null
}): Promise<ReduxPurchase[]> {
	if (!tenantId) {
		return []
	}
	const purchasesArray: ReduxPurchase[] = []
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	let currFilter: ModelPurchaseFilterInput = {
		activeFlag: { eq: true },
		and: [
			{
				createdOn: {
					gt: startDate ?? dayjs().toISOString(),
				},
			},
			{ createdOn: { lt: endDate ?? dayjs().toISOString() } },
			{
				_deleted: {
					attributeExists: false,
				},
			},
		],
	}
	if (!startDate || !endDate) {
		currFilter = {
			activeFlag: { eq: true },
		}
	}
	const paginateFetchPurchases = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const response = (await API.graphql(
			graphqlOperation(queries.purchasesByTenantId, {
				nextToken,
				tenantId,
				filter: currFilter,
				limit: 1000,
			})
		)) as GraphQLResult<PurchasesByTenantIdQuery>
		const purchases =
			response.data?.purchasesByTenantId?.items?.filter(isNonNullable) ??
			[]
		const nextTokenFromApi =
			response.data?.purchasesByTenantId?.nextToken ?? null
		counter++
		purchasesArray.push(...(purchases as unknown as ReduxPurchase[]))
		return paginateFetchPurchases(nextTokenFromApi)
	}
	await paginateFetchPurchases()
	return purchasesArray
}

async function fetchChildFreelancerQuotes({
	tenantId,
	freelancerTenantId,
}: {
	tenantId?: string | null
	freelancerTenantId?: string | null
}): Promise<ReduxQuote[]> {
	if (!tenantId || !freelancerTenantId) {
		return []
	}
	const quotesArray: ReduxQuote[] = []
	let counter = 0
	const childFilter: ModelQuoteFilterInput = {
		activeFlag: { eq: true },
		childFreelancers: { contains: freelancerTenantId },
	}
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchPurchases = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const response = (await API.graphql(
			graphqlOperation(queries.quotesByTenantId, {
				nextToken,
				tenantId,
				filter: childFilter,
				limit: 1000,
			})
		)) as GraphQLResult<QuotesByTenantIdQuery>
		const quotes =
			response.data?.quotesByTenantId?.items?.filter(isNonNullable) ?? []
		const nextTokenFromApi =
			response.data?.quotesByTenantId?.nextToken ?? null
		counter++
		quotesArray.push(...(quotes as unknown as ReduxQuote[]))
		return paginateFetchPurchases(nextTokenFromApi)
	}
	await paginateFetchPurchases()
	return quotesArray
}

async function fetchPurchasesByTenantIdUtilFreelancer({
	tenantId,
	freelancerTenantId,
	startDate = null,
	endDate = null,
}: {
	tenantId?: string | null
	freelancerTenantId?: string | null
	startDate?: string | null
	endDate?: string | null
}): Promise<ReduxPurchase[]> {
	if (!tenantId || !freelancerTenantId) {
		return []
	}
	const purchasesArray: ReduxPurchase[] = []
	let counter = 0
	console.log('Check if this is called')
	const quoteArray = await fetchChildFreelancerQuotes({
		tenantId,
		freelancerTenantId,
	})
	console.log('quoteArray: ', quoteArray)
	// The api call paginates after 1000 items fetched this function is called to get all the values

	let currFilter: ModelPurchaseFilterInput = {
		activeFlag: { eq: true },
		and: [
			{
				createdOn: {
					gt: startDate ?? dayjs().toISOString(),
				},
			},
			{ createdOn: { lt: endDate ?? dayjs().toISOString() } },
			{
				_deleted: {
					attributeExists: false,
				},
			},
		],
		or: getFilterArray(
			'quoteId',
			quoteArray.map((q) => q.id)
		),
	}
	if (!startDate || !endDate) {
		currFilter = {
			activeFlag: { eq: true },
			or: getFilterArray(
				'quoteId',
				quoteArray.map((q) => q.id)
			),
		}
	}

	const paginateFetchPurchases = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const data = await API.graphql(
			graphqlOperation(queries.purchasesByTenantId, {
				nextToken,
				tenantId,
				filter: currFilter,
				limit: 1000,
			})
		)
		const response = data as GraphQLResult<PurchasesByTenantIdQuery>
		const purchases =
			response.data?.purchasesByTenantId?.items?.filter(isNonNullable) ??
			[]
		const nextTokenFromApi =
			response.data?.purchasesByTenantId?.nextToken ?? null
		counter++
		purchasesArray.push(...(purchases as unknown as ReduxPurchase[]))
		return paginateFetchPurchases(nextTokenFromApi)
	}
	await paginateFetchPurchases()
	return purchasesArray
}

async function fetchContactsByTenantIdUtil(
	tenantId: string | null | undefined
): Promise<ReduxContact[]> {
	if (!tenantId) {
		return []
	}
	const contactsArray: ReduxContact[] = []
	let counter = 0
	const contactFilter: ModelContactFilterInput = {
		activeFlag: { eq: true },
	}
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchContacts = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const response = (await API.graphql(
			graphqlOperation(queries.contactsByTenantId, {
				nextToken,
				tenantId,
				filter: contactFilter,
				limit: 1000,
			})
		)) as GraphQLResult<ContactsByTenantIdQuery>
		const contacts =
			response.data?.contactsByTenantId?.items?.filter(isNonNullable) ??
			[]
		const nextTokenFromApi =
			response.data?.contactsByTenantId?.nextToken ?? null
		counter++
		contactsArray.push(...(contacts as unknown as ReduxContact[]))
		return paginateFetchContacts(nextTokenFromApi)
	}
	await paginateFetchContacts()
	return contactsArray
}
async function fetchProposalsByTenantIdUtil(
	tenantId: string | null | undefined
): Promise<ReduxProposals[]> {
	if (!tenantId) {
		return []
	}
	const proposalsArray: ReduxProposals[] = []
	let counter = 0
	const proposalsFilter: ModelProposalsFilterInput = {
		activeFlag: { eq: true },
	}
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchProposals = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const response = (await API.graphql(
			graphqlOperation(queries.proposalsByTenantId, {
				nextToken,
				tenantId,
				filter: proposalsFilter,
				limit: 1000,
			})
		)) as GraphQLResult<ProposalsByTenantIdQuery>
		const proposals =
			response.data?.proposalsByTenantId?.items?.filter(isNonNullable) ??
			[]
		const nextTokenFromApi =
			response.data?.proposalsByTenantId?.nextToken ?? null
		counter++
		proposalsArray.push(...(proposals as unknown as ReduxProposals[]))
		return paginateFetchProposals(nextTokenFromApi)
	}
	await paginateFetchProposals()
	return proposalsArray
}
async function fetchInvoicesByTenantIdUtil(
	tenantId: string | null | undefined,
	startDate?: string | null,
	endDate?: string | null
): Promise<ReduxInvoices[]> {
	if (!tenantId) {
		return []
	}
	const invoicesArray: ReduxInvoices[] = []
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchInvoices = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}

		let currFilter: ModelInvoicesFilterInput = {
			activeFlag: { eq: true },
			and: [
				{
					invoiceDate: {
						gt: startDate ?? dayjs().toISOString(),
					},
				},
				{ invoiceDate: { lt: endDate ?? dayjs().toISOString() } },
				{
					_deleted: {
						attributeExists: false,
					},
				},
			],
		}
		if (!startDate || !endDate) {
			currFilter = { activeFlag: { eq: true } }
		}

		const response = (await API.graphql(
			graphqlOperation(queries.invoicesByTenantId, {
				nextToken,
				tenantId,
				filter: currFilter,
				limit: 1000,
			})
		)) as GraphQLResult<InvoicesByTenantIdQuery>
		const invoices =
			response.data?.invoicesByTenantId?.items?.filter(isNonNullable) ??
			[]
		const nextTokenFromApi =
			response.data?.invoicesByTenantId?.nextToken ?? null
		counter++
		invoicesArray.push(...(invoices as unknown as ReduxInvoices[]))
		return paginateFetchInvoices(nextTokenFromApi)
	}
	await paginateFetchInvoices()
	return invoicesArray
}
async function fetchPurchaseOrdersByTenantIdUtil(
	tenantId: string | null | undefined
): Promise<ReduxPurchaseOrder[]> {
	if (!tenantId) {
		return []
	}
	const purchaseOrdersArray: ReduxPurchaseOrder[] = []
	let counter = 0
	const purchaseOrderFilter: ModelPurchaseOrderFilterInput = {
		activeFlag: { eq: true },
		purchaseOrderNumber: { attributeExists: true },
	}
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchPurchaseOrders = async (
		nextToken: string | null = null
	) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const response = (await API.graphql(
			graphqlOperation(queries.purchaseOrdersByTenantId, {
				nextToken,
				tenantId,
				filter: purchaseOrderFilter,
				limit: 1000,
			})
		)) as GraphQLResult<PurchaseOrdersByTenantIdQuery>
		const purchaseOrders =
			response.data?.purchaseOrdersByTenantId?.items?.filter(
				isNonNullable
			) ?? []
		const nextTokenFromApi =
			response.data?.purchaseOrdersByTenantId?.nextToken ?? null
		counter++
		purchaseOrdersArray.push(
			...(purchaseOrders as unknown as ReduxPurchaseOrder[])
		)
		return paginateFetchPurchaseOrders(nextTokenFromApi)
	}
	await paginateFetchPurchaseOrders()
	return purchaseOrdersArray
}
async function fetchAllPrepareInvoicesByTenantIdUtil(
	tenantId: string | null | undefined
): Promise<ReduxPreparePurchase[]> {
	if (!tenantId) {
		return []
	}
	const prepareInvoiceArray: ReduxPreparePurchase[] = []
	let counter = 0
	const prepareFilter: ModelPreparePurchaseFilterInput = {
		activeFlag: { eq: true },
	}
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchPrepareInvoice = async (
		nextToken: string | null = null
	) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const response = (await API.graphql(
			graphqlOperation(queries.preparePurchaseByTenantId, {
				nextToken,
				tenantId,
				filter: prepareFilter,
				limit: 1000,
			})
		)) as GraphQLResult<PreparePurchaseByTenantIdQuery>
		const prepareInvoice =
			response.data?.preparePurchaseByTenantId?.items?.filter(
				isNonNullable
			) ?? []
		const nextTokenFromApi =
			response.data?.preparePurchaseByTenantId?.nextToken ?? null
		counter++
		prepareInvoiceArray.push(
			...(prepareInvoice as unknown as ReduxPreparePurchase[])
		)
		return paginateFetchPrepareInvoice(nextTokenFromApi)
	}
	await paginateFetchPrepareInvoice()
	return prepareInvoiceArray
}
async function fetchReviewAndExportPrepareInvoicesByTenantIdUtil(
	tenantId: string | null | undefined
): Promise<ReduxPreparePurchase[]> {
	if (!tenantId) {
		return []
	}
	const prepareInvoiceArray: ReduxPreparePurchase[] = []
	let counter = 0
	const prepareFilter: ModelPreparePurchaseFilterInput = {
		activeFlag: { eq: true },
		or: [
			{ currentPage: { eq: 'review' } },
			{ currentPage: { eq: 'export' } },
		],
	}
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchPrepareInvoice = async (
		nextToken: string | null = null
	) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const response = (await API.graphql(
			graphqlOperation(queries.preparePurchaseByTenantId, {
				nextToken,
				tenantId,
				filter: prepareFilter,
				limit: 1000,
			})
		)) as GraphQLResult<PreparePurchaseByTenantIdQuery>
		const prepareInvoice =
			response.data?.preparePurchaseByTenantId?.items?.filter(
				isNonNullable
			) ?? []
		const nextTokenFromApi =
			response.data?.preparePurchaseByTenantId?.nextToken ?? null
		counter++
		prepareInvoiceArray.push(
			...(prepareInvoice as unknown as ReduxPreparePurchase[])
		)
		return paginateFetchPrepareInvoice(nextTokenFromApi)
	}
	await paginateFetchPrepareInvoice()
	return prepareInvoiceArray
}

async function fetchUnpaidExportPurchasesByTenantIdUtil(
	tenantId: string | null | undefined
): Promise<ReduxPreparePurchase[]> {
	if (!tenantId) {
		return []
	}
	const prepareInvoiceArray: ReduxPreparePurchase[] = []
	let counter = 0
	const prepareFilter: ModelPreparePurchaseFilterInput = {
		activeFlag: { eq: true },
		currentPage: { eq: 'export' },
		paymentStatus: { ne: 'Paid' },
	}
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchPrepareInvoice = async (
		nextToken: string | null = null
	) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const response = (await API.graphql(
			graphqlOperation(queries.preparePurchaseByTenantId, {
				nextToken,
				tenantId,
				filter: prepareFilter,
				limit: 1000,
			})
		)) as GraphQLResult<PreparePurchaseByTenantIdQuery>
		const prepareInvoice =
			response.data?.preparePurchaseByTenantId?.items?.filter(
				isNonNullable
			) ?? []
		const nextTokenFromApi =
			response.data?.preparePurchaseByTenantId?.nextToken ?? null
		counter++
		prepareInvoiceArray.push(
			...(prepareInvoice as unknown as ReduxPreparePurchase[])
		)
		return paginateFetchPrepareInvoice(nextTokenFromApi)
	}
	await paginateFetchPrepareInvoice()
	return prepareInvoiceArray
}

async function fetchPrepareInvoicesByTenantIdUtil(
	tenantId: string | null | undefined,
	pageName: string | null | undefined
): Promise<ReduxPreparePurchase[]> {
	if (!tenantId || !pageName) {
		return []
	}
	const prepareInvoiceArray: ReduxPreparePurchase[] = []
	let counter = 0
	const prepareFilter: ModelPreparePurchaseFilterInput = {
		activeFlag: { eq: true },
		currentPage: { eq: pageName },
	}
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchPrepareInvoice = async (
		nextToken: string | null = null
	) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const response = (await API.graphql(
			graphqlOperation(queries.preparePurchaseByTenantId, {
				nextToken,
				tenantId,
				filter: prepareFilter,
				limit: 1000,
			})
		)) as GraphQLResult<PreparePurchaseByTenantIdQuery>
		const prepareInvoice =
			response.data?.preparePurchaseByTenantId?.items?.filter(
				isNonNullable
			) ?? []
		const nextTokenFromApi =
			response.data?.preparePurchaseByTenantId?.nextToken ?? null
		counter++
		prepareInvoiceArray.push(
			...(prepareInvoice as unknown as ReduxPreparePurchase[])
		)
		return paginateFetchPrepareInvoice(nextTokenFromApi)
	}
	await paginateFetchPrepareInvoice()
	return prepareInvoiceArray
}

async function fetchPurchasesByCostlineIdUtil({
	recapCostlineId,
	filter,
}: {
	recapCostlineId?: string | null
	filter?: ModelPurchaseFilterInput | null
}): Promise<ReduxPurchase[]> {
	if (!recapCostlineId) {
		return []
	}
	const purchasesArray: ReduxPurchase[] = []
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchPurchases = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const response = (await API.graphql(
			graphqlOperation(queries.purchasesByRecapCostlineId, {
				nextToken,
				recapCostlineId,
				filter,
				limit: 1000,
			})
		)) as GraphQLResult<PurchasesByRecapCostlineIdQuery>
		const purchases =
			response.data?.purchasesByRecapCostlineId?.items?.filter(
				isNonNullable
			) ?? []
		const nextTokenFromApi =
			response.data?.purchasesByRecapCostlineId?.nextToken ?? null
		counter++
		purchasesArray.push(...(purchases as unknown as ReduxPurchase[]))
		return paginateFetchPurchases(nextTokenFromApi)
	}
	await paginateFetchPurchases()
	return purchasesArray
}
async function fetchAllUserInfos(): Promise<ReduxUserInfo[]> {
	const userInfoArray: ReduxUserInfo[] = []
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchUserInfo = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const response = (await API.graphql(
			graphqlOperation(queries.listUserInfos, {
				nextToken,
				limit: 1000,
			})
		)) as GraphQLResult<ListUserInfosQuery>
		const userInfos =
			response.data?.listUserInfos?.items?.filter(isNonNullable) ?? []
		const nextTokenFromApi = response.data?.listUserInfos?.nextToken ?? null
		counter++
		userInfoArray.push(...(userInfos as unknown as ReduxUserInfo[]))
		return paginateFetchUserInfo(nextTokenFromApi)
	}
	await paginateFetchUserInfo()
	return userInfoArray
}

async function fetchBankTransactionsByTenantIdUtil(
	tenantId: string | null | undefined,
	startDate?: string | null,
	endDate?: string | null
): Promise<ReduxBankTransactions[]> {
	if (!tenantId) {
		return []
	}
	const bankTransactionsArray: ReduxBankTransactions[] = []
	let counter = 0
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchBankTransactions = async (
		nextToken: string | null = null
	) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		let currFilter: ModelBankTransactionsFilterInput = {
			activeFlag: { eq: true },
			and: [
				{
					date: {
						gt: startDate ?? dayjs().toISOString(),
					},
				},
				{ date: { lt: endDate ?? dayjs().toISOString() } },
				{
					_deleted: {
						attributeExists: false,
					},
				},
			],
		}
		if (!startDate || !endDate) {
			currFilter = { activeFlag: { eq: true } }
		}

		const response = (await API.graphql(
			graphqlOperation(queries.bankTransactionsByTenantId, {
				nextToken,
				tenantId,
				filter: currFilter,
				limit: 1000,
			})
		)) as GraphQLResult<BankTransactionsByTenantIdQuery>
		const bankTransactions =
			response.data?.bankTransactionsByTenantId?.items?.filter(
				isNonNullable
			) ?? []
		const nextTokenFromApi =
			response.data?.bankTransactionsByTenantId?.nextToken ?? null
		counter++
		bankTransactionsArray.push(
			...(bankTransactions as unknown as ReduxBankTransactions[])
		)
		return paginateFetchBankTransactions(nextTokenFromApi)
	}
	await paginateFetchBankTransactions()
	return bankTransactionsArray
}

async function fetchLogsByTenantIdUtil(
	tenantId: string | null | undefined
): Promise<ReduxExportInfo[]> {
	if (!tenantId) {
		return []
	}
	const logsArray: ReduxExportInfo[] = []
	let counter = 0
	const exportFilter: ModelExportInfoFilterInput = {
		activeFlag: { eq: true },
	}
	// The api call paginates after 1000 items fetched this function is called to get all the values
	const paginateFetchLogs = async (nextToken: string | null = null) => {
		if (counter > 0 && nextToken === null) {
			return
		}
		const response = (await API.graphql(
			graphqlOperation(queries.exportInfoByTenantId, {
				nextToken,
				tenantId,
				filter: exportFilter,
				limit: 1000,
			})
		)) as GraphQLResult<ExportInfoByTenantIdQuery>
		const logs =
			response.data?.exportInfoByTenantId?.items?.filter(isNonNullable) ??
			[]
		const nextTokenFromApi =
			response.data?.exportInfoByTenantId?.nextToken ?? null
		counter++
		logsArray.push(...(logs as unknown as ReduxExportInfo[]))
		return paginateFetchLogs(nextTokenFromApi)
	}
	await paginateFetchLogs()
	return logsArray
}

function getFetchFilter(
	userInfo: UserInfoLike | null | undefined
): ModelQuoteFilterInput {
	const activeFilter: ModelQuoteFilterInput = { activeFlag: { eq: true } }
	const deletedFilter: ModelQuoteFilterInput = {
		_deleted: { attributeExists: false },
	}
	if (!userInfo) {
		return { and: [activeFilter, deletedFilter] }
	}
	const groups = getUserGroups(userInfo)
	if (
		['companyAdmin', 'subTeamAdmin', 'teamAdmin'].some((group) =>
			groups.includes(group)
		)
	) {
		const filters: ModelQuoteFilterInput[] = [activeFilter]
		if (userInfo.currentCompanyTenantId) {
			filters.push({
				tenantId: { eq: userInfo.currentCompanyTenantId },
			})
		}
		filters.push(deletedFilter)
		return { and: filters }
	}
	if (groups.includes('freelancer') && userInfo.tenantId) {
		return {
			and: [
				activeFilter,
				{ childFreelancers: { contains: userInfo.tenantId } },
				deletedFilter,
			],
		}
	}
	return { and: [activeFilter, deletedFilter] }
}

// QuoteInfo/RecapInfo fetches are always scoped to a single quote whose
// access has already been checked (checkIfItemIsAccessible / the quote list
// filters). Freelancer access is decided at the quote level: the per-costline
// childFreelancers copies can go stale (e.g. after a partially-failed move),
// and filtering on them would silently hide legitimate rows from the grids.
function getCostlineFetchFilter(
	userInfo: UserInfoLike | null | undefined
): ModelQuoteFilterInput {
	if (userInfo) {
		const groups = getUserGroups(userInfo)
		const isAdmin = ['companyAdmin', 'subTeamAdmin', 'teamAdmin'].some(
			(group) => groups.includes(group)
		)
		if (!isAdmin && groups.includes('freelancer')) {
			return {
				and: [
					{ activeFlag: { eq: true } },
					{ _deleted: { attributeExists: false } },
				],
			}
		}
	}
	return getFetchFilter(userInfo)
}

function checkIfItemIsAccessible<T extends AccessibleItem>(
	userInfo: ReduxUserInfo | null | undefined,
	item: T | null | undefined
): T | null {
	if (!userInfo || !item) {
		return null
	}
	const groups = getUserGroups(userInfo)
	if (groups.includes('companyAdmin') || groups.includes('support')) {
		return item.tenantId && userInfo.currentCompanyTenantId
			? item.tenantId === userInfo.currentCompanyTenantId
				? item
				: null
			: null
	}
	if (groups.includes('freelancer')) {
		const childFreelancers = Array.isArray(item.childFreelancers)
			? item.childFreelancers.filter(isNonNullable)
			: []
		return userInfo.tenantId && childFreelancers.includes(userInfo.tenantId)
			? item
			: null
	}
	if (groups.includes('teamAdmin')) {
		const teamIds = Array.isArray(userInfo.teamIds)
			? userInfo.teamIds.filter(isNonNullable)
			: []
		return item.parentTeamId && teamIds.includes(item.parentTeamId)
			? item
			: null
	}
	if (groups.includes('subTeamAdmin')) {
		const subTeamIds = Array.isArray(userInfo.subTeamIds)
			? userInfo.subTeamIds.filter(isNonNullable)
			: []
		return item.parentSubTeamId && subTeamIds.includes(item.parentSubTeamId)
			? item
			: null
	}
	return null
}

function checkIfTemplateIsAccessible<T extends TemplateAccessibleItem>({
	userInfo,
	item,
	subTeamIds,
}: {
	userInfo: ReduxUserInfo | null | undefined
	item: T | null | undefined
	subTeamIds: Array<string | null> | null | undefined
}): T | null {
	if (!userInfo || !item) {
		return null
	}
	const groups = getUserGroups(userInfo)
	if (groups.includes('companyAdmin') || groups.includes('support')) {
		return item.tenantId && userInfo.currentCompanyTenantId
			? item.tenantId === userInfo.currentCompanyTenantId
				? item
				: null
			: null
	}
	if (groups.includes('teamAdmin')) {
		const userTeamIds = Array.isArray(userInfo.teamIds)
			? userInfo.teamIds.filter(isNonNullable)
			: []
		const itemTeamIds = Array.isArray(item.teamIds)
			? item.teamIds.filter(isNonNullable)
			: []
		return itemTeamIds.some((id) => userTeamIds.includes(id)) ? item : null
	}
	if (groups.includes('subTeamAdmin')) {
		const userSubTeamIds = Array.isArray(userInfo.subTeamIds)
			? userInfo.subTeamIds.filter(isNonNullable)
			: []
		const itemSubTeamIds = Array.isArray(item.subTeamIds)
			? item.subTeamIds.filter(isNonNullable)
			: []
		return itemSubTeamIds.some((id) => userSubTeamIds.includes(id))
			? item
			: null
	}
	if (groups.includes('freelancer')) {
		const userSubTeamIds = Array.isArray(userInfo.subTeamIds)
			? userInfo.subTeamIds.filter(isNonNullable)
			: []
		const providedSubTeams = Array.isArray(subTeamIds)
			? subTeamIds.filter(isNonNullable)
			: []
		return providedSubTeams.some((id) => userSubTeamIds.includes(id))
			? item
			: null
	}
	return null
}

function getFilterArray(
	fieldName: keyof ModelPurchaseFilterInput,
	searchArray: Array<string | null | undefined>
): ModelPurchaseFilterInput['or'] {
	return searchArray
		.filter(
			(item): item is string =>
				typeof item === 'string' && item.length > 0
		)
		.map((value) => ({
			[fieldName]: { contains: value },
		})) as Array<ModelPurchaseFilterInput>
}

function filteredArrayItemsFromUserInfo<T extends AccessibleItem>({
	items,
	userInfo,
}: {
	items: T[]
	userInfo: UserInfoLike | null | undefined
}): T[] {
	if (!userInfo) {
		return items
	}
	const groups = getUserGroups(userInfo)
	if (groups.includes('companyAdmin') || groups.includes('freelancer')) {
		return items
	}
	if (groups.includes('teamAdmin')) {
		const teamIds = Array.isArray(userInfo.teamIds)
			? userInfo.teamIds.filter(isNonNullable)
			: []
		const filteredItems = items.filter((item) =>
			item.parentTeamId ? teamIds.includes(item.parentTeamId) : false
		)
		console.log('userInfo: ', userInfo)
		return filteredItems
	}
	if (groups.includes('subTeamAdmin')) {
		const subTeamIds = Array.isArray(userInfo.subTeamIds)
			? userInfo.subTeamIds.filter(isNonNullable)
			: []
		return items.filter((item) =>
			item.parentSubTeamId
				? subTeamIds.includes(item.parentSubTeamId)
				: false
		)
	}
	return items
}

export {
	checkIfItemIsAccessible,
	checkIfTemplateIsAccessible,
	fetchAllPrepareInvoicesByTenantIdUtil,
	fetchAllProjectsByTenantIdUtil,
	fetchAllQuotesByTenantIdUtil,
	fetchAllQuotesByTenantIdUtilForSubTeamAdmin,
	fetchAllQuotesByTenantIdUtilForTeamAdmin,
	fetchAllQuotesByTenantIdUtilNew,
	fetchAllUserInfos,
	fetchArchiveProjectsByTenantIdUtil,
	fetchBankTransactionsByTenantIdUtil,
	fetchChildFreelancerProjectUtil,
	fetchChildFreelancerQuotes,
	fetchClientsUtil,
	fetchClientsUtilForTeamAdmin,
	fetchContactsByTenantIdUtil,
	fetchFreelancerProjectUtil,
	fetchInvoicesByTenantIdUtil,
	fetchLogsByTenantIdUtil,
	fetchPrepareInvoicesByTenantIdUtil,
	fetchProjectsByClientIdUtil,
	fetchProjectsByParentProjectIdUtil,
	fetchProjectsByTenantIdUtil,
	fetchProjectsByTenantIdUtilForSubTeamAdmin,
	fetchProjectsByTenantIdUtilForTeamAdmin,
	fetchProposalsByTenantIdUtil,
	fetchPurchaseOrdersByTenantIdUtil,
	fetchPurchasesByCostlineIdUtil,
	fetchPurchasesByTenantIdUtil,
	fetchPurchasesByTenantIdUtilFreelancer,
	fetchQuoteInfoUtil,
	fetchQuotesByParentProjectIdUtil,
	fetchQuotesByTenantIdUtil,
	fetchRecapInfoUtil,
	fetchRecapInfoWithActiveFlagFalse,
	fetchReviewAndExportPrepareInvoicesByTenantIdUtil,
	fetchSectionRenameUtil,
	fetchSuppliersByTenantIdUtil,
	fetchUnpaidExportPurchasesByTenantIdUtil,
	filteredArrayItemsFromUserInfo,
	getCostlineFetchFilter,
	getFetchFilter,
	getFilterArray,
}
